// ─────────────────────────────────────────────
//  FinancialsTab.js — Tab 3: Financial Statements
// ─────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { fetchIncomeStatement, fetchBalanceSheet, fetchCashFlow } from "../../config/api";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import "./FinancialsTab.css";

function crore(v) {
  const n = Number(v);
  if (!n) return "—";
  if (Math.abs(n) >= 1e7) return (n / 1e7).toFixed(2) + " Cr";
  if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(2) + " L";
  return n.toLocaleString("en-IN");
}
function croreNum(v) { return v ? Number(v) / 1e7 : null; }

// Responsive chart heights
function useIsMobile() {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="fin-tooltip">
      <div className="fin-tt-label">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="fin-tt-row" style={{ color: p.color }}>
          {p.name}: ₹{Number(p.value || 0).toFixed(2)} Cr
        </div>
      ))}
    </div>
  );
};

const PERIOD_OPTIONS = [
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Annual",    value: "ANNUAL"    },
];

export default function FinancialsTab({ symbol, exchange }) {
  const [period,  setPeriod]  = useState("QUARTERLY");
  const [income,  setIncome]  = useState(null);
  const [balance, setBalance] = useState(null);
  const [cashflow,setCashflow]= useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [inc, bal, cf] = await Promise.allSettled([
          fetchIncomeStatement(symbol, exchange, period),
          fetchBalanceSheet(symbol, exchange, period),
          fetchCashFlow(symbol, exchange, period),
        ]);
        if (!cancelled) {
          setIncome( inc.status  === "fulfilled" ? inc.value  : null);
          setBalance(bal.status  === "fulfilled" ? bal.value  : null);
          setCashflow(cf.status  === "fulfilled" ? cf.value   : null);
          if (inc.status === "rejected" && bal.status === "rejected")
            setError("Financial data unavailable for this stock.");
        }
      } catch (e) {
        if (!cancelled) setError("Failed to load financial data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [symbol, exchange, period]);

  // Normalise income statement rows
  const incomeRows = (() => {
    const raw = income?.income_statement || income?.financials || income || [];
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 8).reverse().map((row) => ({
      period: row.period || row.date || row.fiscal_period || "",
      revenue: croreNum(row.total_revenue || row.revenue),
      ebitda:  croreNum(row.ebitda || row.operating_income),
      pat:     croreNum(row.net_income || row.net_profit || row.pat),
    }));
  })();

  const balanceRows = (() => {
    const raw = balance?.balance_sheet || balance?.financials || balance || [];
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 6).reverse().map((row) => ({
      period:      row.period || row.date || row.fiscal_period || "",
      totalAssets: croreNum(row.total_assets),
      totalLiab:   croreNum(row.total_liabilities || row.total_debt),
      equity:      croreNum(row.total_equity || row.stockholders_equity),
    }));
  })();

  const cashRows = (() => {
    const raw = cashflow?.cash_flow || cashflow?.financials || cashflow || [];
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 6).reverse().map((row) => ({
      period:    row.period || row.date || row.fiscal_period || "",
      operating: croreNum(row.operating_cash_flow || row.cash_from_operations),
      investing: croreNum(row.investing_cash_flow || row.cash_from_investing),
      financing: croreNum(row.financing_cash_flow || row.cash_from_financing),
    }));
  })();

  if (loading) return <FinSkeleton />;
  if (error && !income && !balance) return <div className="fin-error">⚠ {error}</div>;

  // Chart heights — smaller on mobile
  const H_INCOME   = isMobile ? 160 : 220;
  const H_BALANCE  = isMobile ? 140 : 200;
  const H_CASHFLOW = isMobile ? 130 : 180;

  // Axis tick props — hide every other tick on mobile
  const xTickProps = isMobile
    ? { fill: "#475569", fontSize: 8, interval: 1 }
    : { fill: "#475569", fontSize: 9 };
  const yTickProps = { fill: "#475569", fontSize: isMobile ? 8 : 9 };

  return (
    <div className="fin-wrap">
      {/* Period toggle */}
      <div className="fin-period-toggle">
        {PERIOD_OPTIONS.map((o) => (
          <button
            key={o.value}
            className={`fin-period-btn ${period === o.value ? "active" : ""}`}
            onClick={() => setPeriod(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* ── Revenue / EBITDA / PAT ── */}
      {incomeRows.length > 0 ? (
        <div className="fin-chart-card">
          <div className="fin-card-title">Revenue, EBITDA &amp; PAT (₹ Crore)</div>
          <ResponsiveContainer width="100%" height={H_INCOME}>
            <BarChart data={incomeRows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }} barGap={2}>
              <CartesianGrid stroke="#1e2d45" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" tick={xTickProps} />
              <YAxis tick={yTickProps} width={isMobile ? 40 : 50}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: isMobile ? "0.65rem" : "0.72rem", color: "#64748b" }} />
              <Bar dataKey="revenue" name="Revenue" fill="#3b82f6"  radius={[3,3,0,0]} maxBarSize={isMobile ? 20 : 32} />
              <Bar dataKey="ebitda"  name="EBITDA"  fill="#22c55e"  radius={[3,3,0,0]} maxBarSize={isMobile ? 20 : 32} />
              <Bar dataKey="pat"     name="PAT"     fill="#f59e0b"  radius={[3,3,0,0]} maxBarSize={isMobile ? 20 : 32} />
            </BarChart>
          </ResponsiveContainer>

          {/* Latest period key metrics */}
          {incomeRows.length > 0 && (() => {
            const latest = incomeRows[incomeRows.length - 1];
            const prev   = incomeRows[incomeRows.length - 2];
            return (
              <div className="fin-summary-row">
                <FinMetric label="Revenue" value={`₹${latest.revenue?.toFixed(2) ?? "—"} Cr`}
                  trend={prev && latest.revenue && prev.revenue ? (((latest.revenue - prev.revenue) / prev.revenue) * 100).toFixed(1) : null} />
                <FinMetric label="EBITDA" value={`₹${latest.ebitda?.toFixed(2) ?? "—"} Cr`}
                  trend={prev && latest.ebitda && prev.ebitda ? (((latest.ebitda - prev.ebitda) / prev.ebitda) * 100).toFixed(1) : null} />
                <FinMetric label="PAT" value={`₹${latest.pat?.toFixed(2) ?? "—"} Cr`}
                  trend={prev && latest.pat && prev.pat ? (((latest.pat - prev.pat) / prev.pat) * 100).toFixed(1) : null} />
              </div>
            );
          })()}
        </div>
      ) : (
        <NoData label="Income Statement" />
      )}

      {/* ── Balance Sheet ── */}
      {balanceRows.length > 0 ? (
        <div className="fin-chart-card">
          <div className="fin-card-title">Balance Sheet (₹ Crore)</div>
          <ResponsiveContainer width="100%" height={H_BALANCE}>
            <BarChart data={balanceRows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }} barGap={2}>
              <CartesianGrid stroke="#1e2d45" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" tick={xTickProps} />
              <YAxis tick={yTickProps} width={isMobile ? 40 : 50}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: isMobile ? "0.65rem" : "0.72rem", color: "#64748b" }} />
              <Bar dataKey="totalAssets" name="Total Assets"  fill="#3b82f6" radius={[3,3,0,0]} maxBarSize={isMobile ? 18 : 28} />
              <Bar dataKey="totalLiab"   name="Liabilities"   fill="#ef4444" radius={[3,3,0,0]} maxBarSize={isMobile ? 18 : 28} />
              <Bar dataKey="equity"      name="Equity"        fill="#22c55e" radius={[3,3,0,0]} maxBarSize={isMobile ? 18 : 28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <NoData label="Balance Sheet" />
      )}

      {/* ── Cash Flow ── */}
      {cashRows.length > 0 ? (
        <div className="fin-chart-card">
          <div className="fin-card-title">Cash Flow (₹ Crore)</div>
          <ResponsiveContainer width="100%" height={H_CASHFLOW}>
            <LineChart data={cashRows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="#1e2d45" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" tick={xTickProps} />
              <YAxis tick={yTickProps} width={isMobile ? 40 : 50}
                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: isMobile ? "0.65rem" : "0.72rem", color: "#64748b" }} />
              <Line type="monotone" dataKey="operating" name="Operating" stroke="#22c55e" dot={isMobile ? false : { r: 3 }} strokeWidth={2} />
              <Line type="monotone" dataKey="investing" name="Investing" stroke="#ef4444" dot={isMobile ? false : { r: 3 }} strokeWidth={2} />
              <Line type="monotone" dataKey="financing" name="Financing" stroke="#3b82f6" dot={isMobile ? false : { r: 3 }} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <NoData label="Cash Flow" />
      )}
    </div>
  );
}

function FinMetric({ label, value, trend }) {
  const up = parseFloat(trend) >= 0;
  return (
    <div className="fin-metric">
      <span className="fin-metric-label">{label}</span>
      <span className="fin-metric-val">{value}</span>
      {trend != null && (
        <span className={`fin-metric-trend ${up ? "up" : "down"}`}>
          {up ? "▲" : "▼"} {Math.abs(trend)}% QoQ
        </span>
      )}
    </div>
  );
}

function NoData({ label }) {
  return (
    <div className="fin-nodata">
      <span>📊</span> {label} data not available for this stock
    </div>
  );
}

function FinSkeleton() {
  return (
    <div className="fin-wrap">
      {[200, 160, 140].map((h, i) => (
        <div key={i} className="fin-chart-card">
          <div className="fin-skel-line w30" style={{ marginBottom: 12 }} />
          <div style={{ height: h, background: "#1e2d45", borderRadius: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
        </div>
      ))}
    </div>
  );
}